import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import PageHeader from "../../components/common/PageHeader";
import Spinner from "../../components/common/Spinner";
import ErrorAlert from "../../components/common/ErrorAlert";
import EmptyState from "../../components/common/EmptyState";
import RegulatoryScheduleForm from "../../components/meetings/RegulatoryScheduleForm";
import { useBuilding } from "../../context/BuildingContext";
import { meetingsApi, contactsApi, buildingsApi } from "../../services/api";
import type {
  Meeting,
  MeetingRegulatoryCategory,
} from "@akfdev-com/shared/src/models/Meeting";
import type { Contact } from "@akfdev-com/shared/src/models/Contact";

interface BuildingNameMap {
  [id: string]: string;
}

interface MeetingFormState {
  title: string;
  date: string;
  chairpersonContactId: string;
  attendeeContactIds: string[];
  location: string;
  agenda: string;
  regulatoryCategory: MeetingRegulatoryCategory | "";
}

const REGULATORY_OPTIONS: {
  value: MeetingRegulatoryCategory;
  label: string;
}[] = [
  { value: "agm", label: "AGM – Annual General Meeting" },
  { value: "service-charge", label: "Service Charge Review" },
  { value: "accounts", label: "Accounts / Budget Approval" },
  { value: "custom", label: "Custom / Other" },
];

const REGULATORY_TITLES: Record<MeetingRegulatoryCategory, string> = {
  agm: "Annual General Meeting",
  "service-charge": "Service Charge Review",
  accounts: "Accounts / Budget Approval",
  custom: "",
};

const defaultForm: MeetingFormState = {
  title: "",
  date: "",
  chairpersonContactId: "",
  attendeeContactIds: [],
  location: "",
  agenda: "",
  regulatoryCategory: "",
};

const MeetingsList: React.FC = () => {
  const { currentBuilding } = useBuilding();
  const navigate = useNavigate();
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [buildingNames, setBuildingNames] = useState<BuildingNameMap>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form, setForm] = useState<MeetingFormState>(defaultForm);

  const buildingId = currentBuilding?.id;
  const isAllBuildings = !buildingId || buildingId === "__ALL__";
  const [showSchedule, setShowSchedule] = useState(false);

  const sortedMeetings = useMemo(
    () => [...meetings].sort((a, b) => a.date.localeCompare(b.date)),
    [meetings],
  );

  const loadMeetings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await meetingsApi.getAll(
        isAllBuildings ? undefined : buildingId,
      );
      setMeetings(data || []);
    } catch {
      setError("Failed to load meetings.");
    } finally {
      setLoading(false);
    }
  }, [buildingId, isAllBuildings]);

  const loadContacts = useCallback(async () => {
    if (isAllBuildings) {
      setContacts([]);
      return;
    }
    try {
      const data = await contactsApi.getAll();
      setContacts((data || []).filter((c: Contact) => !c.archivedAt));
    } catch {
      // Non-fatal — contacts won't be available for selection
    }
  }, [isAllBuildings]);

  const loadBuildingNames = useCallback(async () => {
    if (!isAllBuildings) return;
    try {
      const data = await buildingsApi.getAll();
      const map: BuildingNameMap = {};
      for (const b of data || []) {
        map[b.id] = b.name;
      }
      setBuildingNames(map);
    } catch {
      // Non-fatal
    }
  }, [isAllBuildings]);

  useEffect(() => {
    loadMeetings();
    loadContacts();
    loadBuildingNames();
  }, [loadMeetings, loadContacts, loadBuildingNames]);

  const handleRegulatoryChange = (category: MeetingRegulatoryCategory | "") => {
    setForm((prev) => ({
      ...prev,
      regulatoryCategory: category,
      title:
        category && category !== "custom"
          ? REGULATORY_TITLES[category]
          : prev.title,
    }));
  };

  const toggleAttendee = (contactId: string) => {
    setForm((prev) => ({
      ...prev,
      attendeeContactIds: prev.attendeeContactIds.includes(contactId)
        ? prev.attendeeContactIds.filter((id) => id !== contactId)
        : [...prev.attendeeContactIds, contactId],
    }));
  };

  const handleCreate = async () => {
    if (isAllBuildings) {
      setError("Select a specific building before creating a meeting.");
      return;
    }

    if (
      !form.title ||
      !form.date ||
      !form.chairpersonContactId ||
      form.attendeeContactIds.length === 0
    ) {
      setError(
        "Title, date, chairperson and at least one attendee are required.",
      );
      return;
    }

    setIsSubmitting(true);
    setError(null);

    const isRegulatory = form.regulatoryCategory !== "";

    try {
      const created = await meetingsApi.create({
        buildingId,
        title: form.title,
        date: form.date,
        location: form.location || undefined,
        agenda: form.agenda || undefined,
        chairpersonContactId: form.chairpersonContactId,
        attendeeContactIds: form.attendeeContactIds,
        regulatorySchedule: {
          isRegulatory,
          category: isRegulatory ? form.regulatoryCategory : "custom",
          source: isRegulatory ? "template" : "manual",
        },
      });

      setMeetings((prev) => [...prev, created]);
      setForm(defaultForm);
      setIsCreating(false);
    } catch {
      setError("Failed to create meeting.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return <Spinner message="Loading meetings..." />;
  }

  return (
    <div className="">
      <PageHeader
        title="Meetings"
        description="Schedule meetings, track attendance, and run mandatory recordings."
        actions={
          <div className="flex items-center gap-2">
            {!isAllBuildings && (
              <button
                type="button"
                onClick={() => {
                  setError(null);
                  setShowSchedule((prev) => !prev);
                  setIsCreating(false);
                }}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                {showSchedule ? "Hide Schedule" : "Regulatory Schedule"}
              </button>
            )}
            {!isAllBuildings && (
              <button
                type="button"
                onClick={() => {
                  setError(null);
                  setShowSchedule(false);
                  setIsCreating((prev) => !prev);
                }}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700"
              >
                {isCreating ? "Cancel" : "New Meeting"}
              </button>
            )}
          </div>
        }
      />

      {error && (
        <div className="mt-4">
          <ErrorAlert message={error} />
        </div>
      )}

      {showSchedule && !isAllBuildings && (
        <div className="mt-6">
          <RegulatoryScheduleForm
            buildingId={buildingId!}
            onMeetingsCreated={() => {
              loadMeetings();
              setShowSchedule(false);
            }}
          />
        </div>
      )}

      {isCreating && !isAllBuildings && (
        <div className="mt-6 bg-white shadow sm:rounded-lg p-5 space-y-4">
          <h2 className="text-base font-semibold text-gray-900">
            Create Meeting
          </h2>

          {/* Regulatory category selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Meeting Type
            </label>
            <select
              value={form.regulatoryCategory}
              onChange={(e) =>
                handleRegulatoryChange(
                  e.target.value as MeetingRegulatoryCategory | "",
                )
              }
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-sm"
            >
              <option value="">Standard (non-regulatory)</option>
              {REGULATORY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Title
              </label>
              <input
                value={form.title}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, title: e.target.value }))
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                placeholder="Quarterly service charge review"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Date
              </label>
              <input
                type="date"
                value={form.date}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, date: e.target.value }))
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Location
              </label>
              <input
                value={form.location}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, location: e.target.value }))
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                placeholder="Community Hall / Zoom"
              />
            </div>
          </div>

          {/* Chairperson dropdown */}
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Chairperson
            </label>
            {contacts.length === 0 ? (
              <p className="mt-1 text-sm text-amber-600">
                No contacts found.{" "}
                <button
                  type="button"
                  onClick={() => navigate("/dashboard/contacts")}
                  className="underline hover:text-amber-800"
                >
                  Add contacts first
                </button>
              </p>
            ) : (
              <select
                value={form.chairpersonContactId}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    chairpersonContactId: e.target.value,
                  }))
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-sm"
              >
                <option value="">Select chairperson…</option>
                {contacts.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Attendees checkboxes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Attendees
            </label>
            {contacts.length === 0 ? (
              <p className="text-sm text-amber-600">
                No contacts available. Add contacts to select attendees.
              </p>
            ) : (
              <div className="border border-gray-200 rounded-md p-3 space-y-2 max-h-48 overflow-y-auto">
                {contacts.map((c) => (
                  <label
                    key={c.id}
                    className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={form.attendeeContactIds.includes(c.id)}
                      onChange={() => toggleAttendee(c.id)}
                      className="rounded border-gray-300 text-primary-600"
                    />
                    {c.name}
                    {c.type && (
                      <span className="text-xs text-gray-400 capitalize">
                        ({c.type})
                      </span>
                    )}
                  </label>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Agenda
            </label>
            <textarea
              rows={4}
              value={form.agenda}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, agenda: e.target.value }))
              }
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
              placeholder="Budget approval, service charge variance review, contractor updates"
            />
          </div>

          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleCreate}
              disabled={isSubmitting}
              className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-60"
            >
              {isSubmitting ? "Creating..." : "Create Meeting"}
            </button>
          </div>
        </div>
      )}

      <div className="mt-6">
        {sortedMeetings.length === 0 ? (
          <EmptyState
            title="No meetings yet"
            description={
              isAllBuildings
                ? "No meetings found across any buildings."
                : "Create your first meeting to start recording transcripts and attendance."
            }
            actionText={isAllBuildings ? undefined : "Create meeting"}
            onAction={isAllBuildings ? undefined : () => setIsCreating(true)}
          />
        ) : (
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <ul className="divide-y divide-gray-200">
              {sortedMeetings.map((meeting) => (
                <li key={meeting.id}>
                  <Link
                    to={`/dashboard/meetings/${meeting.id}`}
                    state={
                      isAllBuildings
                        ? { buildingId: meeting.buildingId }
                        : undefined
                    }
                    className="block px-4 py-4 sm:px-6 hover:bg-gray-50"
                  >
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <p className="text-sm font-semibold text-primary-700">
                          {meeting.title}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {meeting.date}
                          {meeting.location ? ` · ${meeting.location}` : ""}
                          {isAllBuildings && meeting.buildingId && (
                            <span className="ml-2 text-gray-400">
                              ·{" "}
                              {buildingNames[meeting.buildingId] ??
                                meeting.buildingId}
                            </span>
                          )}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-500">Recording</p>
                        <p className="text-sm font-medium text-gray-800 capitalize">
                          {meeting.recordingStatus}
                        </p>
                      </div>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default MeetingsList;
